import { FC, useEffect, useState } from 'react';
import { Box, Grid, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  FooterContainer,
  footerCopyright,
  FooterLink,
  FooterLinksContainer,
  FooterSectionContainer,
  MenuContainer,
  MenuLink,
  MenuSectionContainer,
  MenuTitle
} from './Footer.styles';
import useFooterLinks from '@/hooks/useFooterLinks';
import useFooterMenuLinks from '@/hooks/useFooterMenuLinks';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { useAppContext } from '@/context/App.context';
import { sopClient } from '@barracuda/shared';

export const Footer: FC = () => {
  const { isLoggedIn } = useAppContext();
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation();
  const footerLinks = useFooterLinks();
  const footerMenuLinks = useFooterMenuLinks();
  const [icpLicenseNumber, setICPLicenseNumber] = useState<string | null>(null);

  const handleNavigate = (url?: string) => {
    if (!url) return;

    const link = isLoggedIn ? `/dashboard/${url}` : `/${url}`;
    navigate(link);
  };

  useEffect(() => {
    (async () => {
      setICPLicenseNumber(await sopClient.fetchICPLicenseNumber());
    })();
  }, []);

  return (
    <Box component='footer'>
      <Box sx={MenuContainer}>
        <Grid sx={MenuSectionContainer} container direction='column'>
          <Grid item xs>
            <Typography sx={MenuTitle}>{t('menuLabels.menuTitle')}</Typography>
          </Grid>
          {footerMenuLinks.map((el, index) => {
            return (
              <Grid item xs key={`menu-link-${index}`}>
                {!!el.to && (
                  <Link sx={MenuLink} onClick={() => handleNavigate(el.to)}>
                    {el.label}
                  </Link>
                )}
                {!!el.href && (
                  <Link sx={MenuLink} href={el.href} target='_blank'>
                    {el.label}
                  </Link>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Box sx={FooterContainer}>
        <Grid sx={FooterSectionContainer} container>
          <Grid item xs={12} md={6}>
            <Box sx={FooterLinksContainer}>
              {footerLinks.map((el, index) => {
                const action =
                  el.type === 'link'
                    ? () => (el.external ? window.open(el.to, '_blank') : handleNavigate(el.to))
                    : el.action;
                return (
                  <Link sx={FooterLink} key={`footer-link-${index}`} onClick={action}>
                    {el.label}
                  </Link>
                );
              })}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography sx={footerCopyright}>
              {t('allRightsReserved', { year: new Date().getFullYear() })}
              &nbsp;
              {icpLicenseNumber && t('icpRegistration', { ICPLicenseNumber: icpLicenseNumber })}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
